import _ from 'lodash';
import { differenceInDays } from 'date-fns';
import GLOBALS from 'routes/marketplace-ede/web-ui/constants/globals';
import {
  EdeMemberType,
  MemberAttestationDemographicType,
  AddressFieldsType,
} from 'routes/marketplace-ede/types/ede/edeMember';
import {
  getMemberMedicaidStatus,
  getMemberEmergencyMedicaidStatus,
  isMemberRequestingCoverage,
  isRequestingFinancialAssistance,
  getTaxHouseholdMemberIdentifiers,
  hasAttestedToSSN,
  getMemberDob,
  getPrimaryMember,
  getMemberAge as getMemberAgeUtil,
} from 'routes/marketplace-ede/utils/selectors';
import { MEDICAID_STATUS_REASONS } from 'routes/marketplace-ede/constants/ede/eligibility';
import { HOME_FIELD_MAP } from '../constants/address';
import { mapAddressToFields, mapFieldsToAddress, ADDRESS_TYPE } from '../utils/address';
import { Profile, ProfileAddress } from '../types/profile/profile';
import { EdeUpdateAppPayload, PhoneNumberFields, PrimaryAttestation } from '../types/base/edeApplication';
import { EdeApplicationPayloadType, TaxRelationshipAttestationType } from '../types/ede/edeApplication';
import { DocumentType, IdProofing, IdProofingDocTypeInput } from '../types/ede/idVerify';
import { slashDateToISO } from '../web-ui/utils/format';

export const MEMBER_COMPUTED_PATH = 'computed';
const { YES_VALUE } = GLOBALS;

type FormatContactPhones = {
  primaryPhoneNumber: PhoneNumberFields | null;
  secondaryPhoneNumber: PhoneNumberFields | null;
};

type Phones = {
  phoneType: string;
  number: string;
  ext: string | null;
};

type FormattedAddresses = {
  homeAddress?: AddressFieldsType;
  mailingAddress?: AddressFieldsType;
  transientAddress?: AddressFieldsType;
};

export const formatApplicantAddress = (addresses: ProfileAddress[]): FormattedAddresses => {
  const homeAddress = _.find(addresses, addr => addr.addressType === ADDRESS_TYPE.HOME);
  const mailingAddress = _.find(addresses, addr => addr.addressType === ADDRESS_TYPE.MAILING);
  const transientAddress = _.find(addresses, addr => addr.addressType === ADDRESS_TYPE.TRANSIENT);
  const mappedType = 'attestation';

  return {
    homeAddress:
      homeAddress &&
      (mapFieldsToAddress(
        mapAddressToFields(homeAddress, 'profile'),
        mappedType,
        HOME_FIELD_MAP,
      ) as AddressFieldsType),
    mailingAddress:
      mailingAddress &&
      (mapFieldsToAddress(
        mapAddressToFields(mailingAddress, 'profile'),
        mappedType,
        HOME_FIELD_MAP,
      ) as AddressFieldsType),
    transientAddress:
      transientAddress &&
      (mapFieldsToAddress(
        mapAddressToFields(transientAddress, 'profile'),
        mappedType,
        HOME_FIELD_MAP,
      ) as AddressFieldsType),
  };
};

export const getMemberMedicaidStatusReason = (member: EdeMemberType) =>
  _.get(member, `${MEMBER_COMPUTED_PATH}.medicaidStatusReason`);

export const getMemberMedicaidNonMagiReferralStatus = (member: EdeMemberType) =>
  _.get(member, `${MEMBER_COMPUTED_PATH}.medicaidNonMagiReferralStatus`);

/**
 * ui_question_companion_guide
 * Backend Responses for UI #33
  1). individuals whose requestingCoverageIndicator = true
  2). individuals whose Medicaid status is not equal to “yes”;
  3). individuals found not eligible for emergency Medicaid
  4). individuals found ineligible for Medicaid due to a recent loss/denial
  5). individuals found not eligible for Non-MAGI OR they are eligible for Non-MAGI & not applying in an FFE-Determination state (acceptMedicaidEligibilityIndicator = Y)
 */
export const getMedicaidDeterminationMembers = (
  members: Array<EdeMemberType>,
  acceptMedicaidEligibilityIndicator: string,
): Array<EdeMemberType> =>
  _.filter(members, (m: EdeMemberType) => {
    const notDeniedMedicaid =
      getMemberMedicaidStatusReason(m) !==
      MEDICAID_STATUS_REASONS['RECENTLY_DENIED_MDCAID_CHIP_PREVENT_RETURN_TO_STATE'];

    const nonMagiReferralStatusCheck =
      getMemberMedicaidNonMagiReferralStatus(m) !== YES_VALUE || acceptMedicaidEligibilityIndicator !== 'Y';

    return (
      isMemberRequestingCoverage(m) &&
      getMemberMedicaidStatus(m) !== YES_VALUE &&
      getMemberEmergencyMedicaidStatus(m) !== YES_VALUE &&
      notDeniedMedicaid &&
      nonMagiReferralStatusCheck
    );
  });

export const formatContactPhones = (phones: Phones[]): FormatContactPhones => {
  if (_.isEmpty(phones)) {
    return {
      primaryPhoneNumber: null,
      secondaryPhoneNumber: null,
    };
  }
  // phones[0] is always primary phone, phones[1] is always secondary phone
  const primaryPhoneNumber: PhoneNumberFields = phones[0] && {
    type: phones[0].phoneType,
    number: phones[0].number,
    ext: phones[0].ext || null,
  };
  const secondaryPhoneNumber: PhoneNumberFields = phones[1] && {
    type: phones[1].phoneType,
    number: phones[1].number,
    ext: phones[1].ext || null,
  };
  return {
    primaryPhoneNumber,
    secondaryPhoneNumber,
  };
};

// PII data cannot be accessible on the frontend, but does relate to some data from our forms
export const mapPrimaryAttestationsToEdePiiPayload = ({
  personId,
  primaryAttestations,
  primaryInsuredMemberIdentifier,
}: {
  personId: string;
  primaryAttestations: PrimaryAttestation;
  primaryInsuredMemberIdentifier: string;
}) => {
  const { ssnAlternateName, ssnNameSegment } = primaryAttestations;

  const demographicPayload: Partial<MemberAttestationDemographicType> = {};
  if (!_.isEmpty(ssnAlternateName) && !ssnNameSegment) {
    demographicPayload.ssnAlternateName = {
      firstName: ssnAlternateName.firstName,
      lastName: ssnAlternateName.lastName,
      middleName: ssnAlternateName.middleName,
      suffix: ssnAlternateName.suffix,
    };
  }

  return {
    members: [
      {
        personId,
        memberIdentifier: primaryInsuredMemberIdentifier,
        edePayload: {
          demographic: demographicPayload,
        },
      },
    ],
  };
};

export const mapPrimaryAttestationToEdePayload = ({
  profile,
  primaryAttestations,
  primaryInsuredMemberIdentifier,
}: {
  profile: Pick<Profile, 'sex' | 'phones' | 'addresses'>;
  primaryAttestations: PrimaryAttestation;
  primaryInsuredMemberIdentifier: string;
}): EdeUpdateAppPayload => {
  const {
    hispanicOriginIndicator,
    ethnicity,
    race,
    isApplying,
    coverageState,
    preferredSpokenLanguage,
    preferredWrittenLanguage,
    preferredContactMethod,
    eTextPhoneNumber,
    sexAssignedAtBirth,
    sexAssignedAtBirthOtherText,
    gender,
    genderDifferentTermText,
    sexualOrientation,
    sexualOrientationDifferentTermText,
  } = primaryAttestations;
  const { sex, addresses, phones } = profile;
  const { primaryPhoneNumber } = formatContactPhones(phones);
  const demographicPayload: Partial<MemberAttestationDemographicType> = {
    sex,
    sexAssignedAtBirth: sexAssignedAtBirth || null,
    sexAssignedAtBirthOtherText: sexAssignedAtBirthOtherText || null,
    gender: gender || null,
    genderDifferentTermText: genderDifferentTermText || null,
    sexualOrientation: sexualOrientation || null,
    sexualOrientationDifferentTermText: sexualOrientationDifferentTermText || null,
  };
  if (_.isBoolean(hispanicOriginIndicator)) {
    demographicPayload.hispanicOriginIndicator = hispanicOriginIndicator;
  }

  // TODO: update race, otherRaceText & ethnicity attestations
  if (!_.isEmpty(ethnicity)) {
    const { ethnicities, other } = ethnicity;
    if (hispanicOriginIndicator) {
      if (!_.isEmpty(ethnicities)) {
        demographicPayload.ethnicity = _.map(ethnicities, e => e.id);
      }
      if (other && _.includes(demographicPayload.ethnicity, 'OTHER')) {
        demographicPayload.otherEthnicityText = other;
      }
    }
  }
  if (!_.isEmpty(race)) {
    if (!_.isEmpty(race.races)) {
      demographicPayload.race = _.map(race.races, r => r.id);
    }
    if (race.other && _.includes(demographicPayload.race, 'OTHER')) {
      demographicPayload.otherRaceText = race.other;
    }
  }

  const transientAddress = _.find(addresses, { addressType: 'TRANSIENT' }) || {};
  const homeAddress = _.find(addresses, { addressType: 'HOME' });
  const mailingAddress = _.find(addresses, { addressType: 'MAILING' });
  const hasOutOfStateAddress = homeAddress
    ? coverageState !== _.get(homeAddress, 'state')
    : coverageState !== _.get(mailingAddress, 'state');

  demographicPayload.liveOutsideStateTemporarilyIndicator =
    hasOutOfStateAddress && _.get(transientAddress, 'state') === coverageState;

  const formattedAddresses = formatApplicantAddress(addresses);

  if (formattedAddresses.homeAddress?.streetName1) {
    demographicPayload.homeAddress = formattedAddresses.homeAddress;
  }

  demographicPayload.mailingAddress = formattedAddresses.mailingAddress || formattedAddresses.homeAddress;

  return {
    application: {
      contactInformation: {
        mobileNotificationPhoneNumber: eTextPhoneNumber || null,
        primaryPhoneNumber: primaryPhoneNumber || null,
      },
      spokenLanguageType: preferredSpokenLanguage || null,
      writtenLanguageType: preferredWrittenLanguage || null,
      contactMethod: [preferredContactMethod] || null,
    },
    members: [
      {
        memberIdentifier: primaryInsuredMemberIdentifier,
        edePayload: {
          demographic: demographicPayload,
          requestingCoverageIndicator: isApplying,
        },
      },
    ],
  };
};

export const mapIdProofingToDocumentPayload = (idProofingData: IdProofing): IdProofingDocTypeInput => {
  const { documents } = idProofingData;
  const documentPayload = documents
    ? documents.reduce((payload, d) => {
      // eslint-disable-next-line no-param-reassign
      if (d.documentNumber) payload[d.documentType] = d.documentNumber;
      return payload;
    }, {} as Record<DocumentType, string>)
    : {};

  return documentPayload;
};

export const isMultiTaxHouseholdEdeApp = (edeApplication: EdeApplicationPayloadType) => {
  const multiTaxHouseholdIndicator = _.get(
    edeApplication,
    'computed.application.multiTaxHouseholdIndicator',
    false,
  );

  const taxHouseholds = _.get(edeApplication, 'computed.taxHouseholds', []);

  // if there are more than one instance where the taxHouseHoldStatus indicator = “yes” then the household is a multi-tax household.
  const taxHouseholdStatusesWithYes = _.filter(
    taxHouseholds,
    household => _.get(household, 'taxHouseHoldComposition.taxHouseHoldStatus') === 'YES',
  ).length;

  return (
    isRequestingFinancialAssistance(edeApplication) &&
    (multiTaxHouseholdIndicator || taxHouseholdStatusesWithYes > 1)
  );
};

export const removeSpouseFromTaxRelationships = (
  currMember: EdeMemberType,
  relationships: TaxRelationshipAttestationType[],
  spouse: EdeMemberType,
) =>
  relationships
    .filter(
      relationship =>
        !(
          relationship.memberIdentifier === currMember.memberIdentifier &&
          relationship.subMemberIdentifier === spouse.memberIdentifier
        ),
    )
    // Filter to remove all tax dependents of spouse when filing jointly
    .filter(
      relationship =>
        !(
          relationship.memberIdentifier === spouse.memberIdentifier &&
          relationship.subMemberIdentifier !== spouse.memberIdentifier
        ),
    );

export const determineSepEligibility = (edeMember: EdeMemberType) => {
  const {
    citizenshipStatus,
    qhpLawfulPresenceStatus,
    incarcerationStatus,
    preliminaryChipStatus,
    preliminaryMedicaidStatus,
    qhpResidency,
  } = edeMember.computed;

  const prelimQHP =
    (citizenshipStatus === 'YES' || qhpLawfulPresenceStatus === 'YES') &&
    qhpResidency?.qhpResidencyStatus === 'YES' &&
    incarcerationStatus === 'NO' &&
    preliminaryChipStatus !== 'YES' &&
    preliminaryMedicaidStatus !== 'YES';

  return prelimQHP;
};

export const sepEligibilityStatus = (edeApplication: EdeApplicationPayloadType) => {
  const taxHouseHoldIds = getTaxHouseholdMemberIdentifiers(edeApplication) || [];
  const members = edeApplication.edeMembers;

  // If the ede app is a non financial assistance application the taxHousehold array will be empty
  const eligibleMembers = isRequestingFinancialAssistance(edeApplication)
    ? members.filter(member => taxHouseHoldIds.includes(member.memberIdentifier))
    : members;

  const sepEligibleMembers = eligibleMembers.filter(member => determineSepEligibility(member));
  const notSepEligibleMembers = eligibleMembers.filter(member => !determineSepEligibility(member));

  return {
    sepEligibleMembers,
    notSepEligibleMembers,
    sepStatus: eligibleMembers.some(member => determineSepEligibility(member)),
  };
};

type HouseholdQhpStatus = {
  qhpEligible: boolean;
  qhpIneligibleReasons: string[];
};

/**
 * A household is consider Qhp eligible if at least one member has `qhpStatus` true
 */
export const getHouseholdQhpStatus = (edeApplication: EdeApplicationPayloadType) => {
  const initialQhpData = { qhpEligible: false, qhpIneligibleReasons: [] };
  if (!edeApplication.edeMembers.length) return initialQhpData;

  /* eslint-disable no-param-reassign */
  return edeApplication.edeMembers.reduce((accum, member) => {
    const { qhpStatus, qhpStatusReason } = member.computed;

    if (qhpStatus === YES_VALUE) {
      accum.qhpEligible = true;
    } else if (qhpStatusReason) {
      accum.qhpIneligibleReasons.push(qhpStatusReason);
    }
    return accum;
  }, initialQhpData as HouseholdQhpStatus);
  /* eslint-enable no-param-reassign */
};

/**
 * A whole house is SEP eligible, all members who are eligible are listed in bestSEP -> associatedMemberIdentifiers
 */
export const getHouseholdSepStatus = (edeApplication: EdeApplicationPayloadType) => {
  const initialSepData = { sepEligible: '', sepType: '', sepEligibleReason: '' };

  const bestSep = _.get(edeApplication.computed.application, 'bestSEP', null);
  if (!bestSep) return initialSepData;

  const {
    sepType,
    status: sepEligible,
    statusReason: sepEligibleReason,
  } = _.pick(bestSep, ['sepType', 'status', 'statusReason']);

  return { sepType, sepEligible, sepEligibleReason };
};

export const isOldEnoughForSSNVerification = (birthDate?: string) => {
  if (!birthDate) return false;
  return differenceInDays(new Date(), slashDateToISO(birthDate)) > 90;
};

// [AGENT/BROKER ONLY] Members who are requesting coverage and have attested to their SSN and are 91 days or older are eligible for SSN verification
export const isEligibleForSSNVerification = (member: EdeMemberType) => {
  const isRequestingCoverage = isMemberRequestingCoverage(member);
  const hasSSN = hasAttestedToSSN(member);
  const birthDate = getMemberDob(member);

  return hasSSN && isOldEnoughForSSNVerification(birthDate) && isRequestingCoverage;
};

export const isEligibleForMedicareMessaging = (edeApplication: EdeApplicationPayloadType) =>
  edeApplication.edeMembers.some(member =>
    member.attestations.requestingCoverageIndicator === true &&
    member.attestations.terminateCoverageStartMedicareIndicator === false);

export const getEdeApplicationAddress = (edeApplication: EdeApplicationPayloadType) => {
  const primaryMember = getPrimaryMember(edeApplication) as EdeMemberType;
  const {
    homeAddress,
    mailingAddress,
    noHomeAddressIndicator,
    liveOutsideStateTemporarilyIndicator,
    transientAddress,
  } = primaryMember.attestations.demographic;

  if (liveOutsideStateTemporarilyIndicator) {
    return transientAddress;
  }

  if (noHomeAddressIndicator) {
    return mailingAddress;
  }

  return homeAddress;
};

export const getMemberAge = (
  memberIdentifier: string | undefined,
  edeApplication: EdeApplicationPayloadType,
): number => {
  const member = edeApplication.edeMembers.find(m => m.memberIdentifier === memberIdentifier);
  if (!member) throw new Error(`Member not found: ${memberIdentifier}`);
  return getMemberAgeUtil(member);
};

const PREVIOUS_COVERAGE_REQUIRED_SVIS = ['200_MOVED_TO_NEW_SERVICE_AREA', '203_MARRIAGE'];
export const isPreviousCoverageRequiredSvi = (verificationReason: string) =>
  PREVIOUS_COVERAGE_REQUIRED_SVIS.includes(verificationReason);

export const getCoverage60DayBeforeCircumstanceChangeConfirmedIndicator = (
  edeApplication: EdeApplicationPayloadType,
) => edeApplication.computed.application.bestSEP?.coverage60DayBeforeCircumstanceChangeConfirmedIndicator;
